import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import type { CostType, DocMode, QuotationLine } from '../../types/e2i';
import { formatAmount, formatPct, lineAmount, markupOf, numberText, parseNumber, priceFromCost, round4 } from '../../utils/e2i';
import LineItemPicker from './LineItemPicker';

interface Props {
  open: boolean;
  line: QuotationLine | null;
  companyId: number;
  costTypes: CostType[];
  markups: Record<string, number>;
  profitPct: number;
  docMode: DocMode;
  currency: string;
  onClose: () => void;
  onSave: (line: Omit<QuotationLine, 'lineNum'>) => void;
}

type Mode = 'cost' | 'fixed';

/**
 * A priced line as the client sees it. From cost: the price is the unit cost with the cost type's markup and the
 * profit. Fixed price: typed (merged lines, extras); the unit cost is kept only for the margin.
 */
const QuotationLineDialog: React.FC<Props> = ({ open, line, companyId, costTypes, markups, profitPct, docMode, currency, onClose, onSave }) => {
  const [mode, setMode] = useState<Mode>('fixed');
  const [costType, setCostType] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [uom, setUom] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [target, setTarget] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(line ? (line.costType ? 'cost' : 'fixed') : 'fixed');
    setCostType(line?.costType || costTypes[0]?.code || '');
    setDescription(line?.description || '');
    setQuantity(line ? numberText(line.quantity) : '1');
    setUom(line?.uom || '');
    setUnitCost(line && line.unitCost ? numberText(line.unitCost) : '');
    setUnitPrice(line ? numberText(line.unitPrice) : '');
    setTarget((docMode === 'Service' ? line?.account : line?.salesItem) || null);
    setTried(false);
    // Only when the dialog opens or shows another line: typing must not be reset by the page re-rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line]);

  const qty = parseNumber(quantity);
  const cost = unitCost.trim() ? parseNumber(unitCost) : 0;
  const markup = mode === 'cost' ? markupOf(costType, markups, costTypes) : 0;
  const price = mode === 'cost' ? priceFromCost(cost ?? 0, markup, profitPct) : parseNumber(unitPrice);
  const problems: string[] = [];
  if (!description.trim()) problems.push('Type the description the client sees');
  if (qty === null || qty <= 0) problems.push('The quantity must be above 0');
  if (cost === null || cost < 0) problems.push('The unit cost must be 0 or more');
  if (mode === 'cost' && !costType) problems.push('Choose the cost type');
  if (mode === 'fixed' && (price === null || price < 0)) problems.push('Type the unit price');

  const save = () => {
    setTried(true);
    if (problems.length || qty === null || price === null || cost === null) return;
    const unit = round4(price);
    onSave({
      costType: mode === 'cost' ? costType : null,
      description: description.trim(),
      quantity: qty,
      uom: uom.trim() || null,
      unitCost: cost,
      markupPct: markup,
      unitPrice: unit,
      amount: lineAmount(qty, unit),
      salesItem: docMode === 'Service' ? line?.salesItem ?? null : target,
      account: docMode === 'Service' ? target : line?.account ?? null,
    });
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="e2i-q-line-title">
      <DialogTitle id="e2i-q-line-title">{line ? `Quotation line ${line.lineNum}` : 'Add a priced line'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RadioGroup row aria-label="How the line is priced" value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
              <FormControlLabel value="cost" control={<Radio size="small" />} label="From cost (markup and profit)" disabled={!costTypes.length} />
              <FormControlLabel value="fixed" control={<Radio size="small" />} label="Fixed price" />
            </RadioGroup>
          </Grid>
          <Grid item xs={12}>
            <TextField id="e2i-q-line-desc" label="Description (printed for the client)" value={description} onChange={(e) => setDescription(e.target.value.slice(0, 254))} fullWidth required multiline maxRows={3} />
          </Grid>
          {mode === 'cost' ? (
            <Grid item xs={12} sm={6}>
              <TextField id="e2i-q-line-type" select label="Cost type" value={costType} onChange={(e) => setCostType(e.target.value)} fullWidth required>
                {costTypes.map((c) => <MenuItem key={c.code} value={c.code}>{c.name} ({c.code})</MenuItem>)}
              </TextField>
            </Grid>
          ) : null}
          <Grid item xs={6} sm={mode === 'cost' ? 3 : 4}>
            <TextField id="e2i-q-line-qty" label="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} inputProps={{ inputMode: 'decimal' }} fullWidth required />
          </Grid>
          <Grid item xs={6} sm={mode === 'cost' ? 3 : 4}>
            <TextField id="e2i-q-line-uom" label="Unit" value={uom} onChange={(e) => setUom(e.target.value.slice(0, 30))} fullWidth />
          </Grid>
          <Grid item xs={12} sm={mode === 'cost' ? 6 : 4}>
            <TextField
              id="e2i-q-line-cost"
              label={`Unit cost ${currency}`.trim()}
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              helperText={mode === 'fixed' ? 'Optional: for the margin only' : undefined}
              fullWidth
              required={mode === 'cost'}
            />
          </Grid>
          {mode === 'fixed' ? (
            <Grid item xs={12} sm={6}>
              <TextField id="e2i-q-line-price" label={`Unit price ${currency}`.trim()} value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} inputProps={{ inputMode: 'decimal' }} fullWidth required />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" component="div">Unit price</Typography>
              <Typography variant="body2">
                <strong>{formatAmount(price ?? 0)}</strong> {currency} · markup {formatPct(markup)}, profit {formatPct(profitPct)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <LineItemPicker id="e2i-q-line-target" companyId={companyId} docMode={docMode} value={target} onChange={setTarget} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">Amount: <strong>{formatAmount(lineAmount(qty ?? 0, price ?? 0))} {currency}</strong></Typography>
          </Grid>
        </Grid>
        {tried && problems.length ? <Alert severity="warning" sx={{ mt: 2 }}>{problems.join('; ')}.</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button onClick={save}>{line ? 'Apply' : 'Add line'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default QuotationLineDialog;
